import { useCallback, useState } from 'react';

import { getStorageValue, setStorageValue } from '@helpers/storage';
import { StorageValues } from '@interfaces/storage';

import { TutorialId } from '../../interfaces';

export type TutorialPromptDecisionValue = 'skipped' | TutorialId;

interface StoredTutorialPromptDecision {
  decision: TutorialPromptDecisionValue;
  decidedAt: number;
}

/**
 * Tracks whether the user has already decided (skip, or pick a tutorial) on
 * the guided-tutorial prompt, persisting the decision to localStorage so the
 * prompt never shows again once a decision has been made.
 *
 * @returns `hasDecided` and `recordDecision`, which persists a decision and flips `hasDecided` to true.
 */
export const useTutorialPromptGate = () => {
  const [hasDecided, setHasDecided] = useState<boolean>(
    () =>
      getStorageValue<StoredTutorialPromptDecision>(
        StorageValues.TutorialPromptDecision,
      ) !== null,
  );

  const recordDecision = useCallback(
    (decision: TutorialPromptDecisionValue) => {
      setStorageValue(StorageValues.TutorialPromptDecision, {
        decision,
        decidedAt: Date.now(),
      });
      setHasDecided(true);
    },
    [],
  );

  return { hasDecided, recordDecision };
};
